import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import useNotification from '../../hooks/useNotification';
import { useNavigation } from '../../contexts/NavigationContext';
import _NotificationDropdown from '../notifications/NotificationDropdown';
import _NavigationSettings from './NavigationSettings';
import _Icon from '../AppIcon';
import _Button from '../ui/Button';

const EnhancedNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, signOut, userProfile } = useAuth();
  
  // Use the new notification hook
  const { 
    unreadCount = 0, 
    notifications = [], 
    getHighPriorityNotifications = () => [],
    fetchNotifications = () => Promise.resolve()
  } = useNotification();
  const { 
    _navigationHistory, 
    recentlyVisited, 
    getNavigationSuggestions,
    _navigationPreferences
  } = useNavigation();
  
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isRecentPagesOpen, setIsRecentPagesOpen] = useState(false);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const notificationsRef = useRef(null);
  const userMenuRef = useRef(null);
  const quickActionsRef = useRef(null);
  const recentPagesRef = useRef(null);
  const suggestionsRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Helper function to get the appropriate profile settings URL based on user role
  const getProfileSettingsUrl = () => {
    if (userProfile?.role === 'employer') {
      return '/employer-profile-settings';
    } else if (userProfile?.role === 'va') {
      return '/va-profile-settings';
    }
    // Default fallback
    return '/va-profile-settings';
  };

  // Effect to initialize notifications when user changes
  useEffect(() => {
    if (currentUser?.id) {
      // Fetch notifications when user logs in or changes
      fetchNotifications();
    }
  }, [currentUser?.id, fetchNotifications]);

  // Effect to initialize and clean up notifications
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target)) {
        setIsQuickActionsOpen(false);
      }
      if (recentPagesRef.current && !recentPagesRef.current.contains(event.target)) {
        setIsRecentPagesOpen(false);
      }
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setIsSuggestionsOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    // Use mousedown for better compatibility
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigation items based on user role
  const getNavigationItems = () => {
    if (!currentUser) return []; // Return empty array for non-authenticated users
    
    const userRole = userProfile?.role || currentUser?.role || 'va';
    
    if (userRole === 'va') {
      return [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: 'LayoutDashboard',
          path: '/va-dashboard',
          description: 'Overview of your activity and opportunities',
          badge: null
        },
        {
          id: 'jobs',
          label: 'Jobs',
          icon: 'Briefcase',
          path: '/job-search',
          description: 'Browse and apply to job opportunities',
          badge: null
        },
        {
          id: 'messages',
          label: 'Messages',
          icon: 'MessageSquare',
          path: '/messages',
          description: 'Communicate with employers',
          badge: notifications.filter(n => n.type === 'message' && n.unread).length || null
        },
        {
          id: 'applications',
          label: 'Applications',
          icon: 'FileText',
          path: '/applications',
          description: 'Track your job applications',
          badge: notifications.filter(n => n.type === 'application' && n.unread).length || null
        },
        {
          id: 'profile',
          label: 'My Profile',
          icon: 'User',
          path: '/va-profile',
          description: 'Manage your profile and portfolio',
          badge: null
        },
        {
          id: 'skills',
          label: 'Skills',
          icon: 'Award',
          path: '/up-skills',
          description: 'Complete skills assessments',
          badge: null
        }
      ];
    } else if (userRole === 'employer') {
      return [
       /*  {
          id: 'dashboard',
          label: 'Dashboard',
          icon: 'LayoutDashboard',
          path: '/employer-dashboard',
          description: 'Overview of your hiring activity',
          badge: null
        }, */
        {
          id: 'jobs',
          label: 'Jobs',
          icon: 'Briefcase',
          path: '/employer-dashboard',
          description: 'Manage your job postings',
          badge: notifications.filter(n => n.type === 'job' && n.unread).length || null
        },
        {
          id: 'candidates',
          label: 'Candidates',
          icon: 'Users',
          path: '/candidate-search',
          description: 'Search and manage candidates',
          badge: notifications.filter(n => (n.type === 'application' || n.type === 'candidate') && n.unread).length || null
        },
        {
          id: 'messages',
          label: 'Messages',
          icon: 'MessageSquare',
          path: '/messages',
          description: 'Communicate with candidates',
          badge: notifications.filter(n => n.type === 'message' && n.unread).length || null
        },
        {
          id: 'interviews',
          label: 'Interviews',
          icon: 'Calendar',
          path: '/interview-management',
          description: 'Schedule and manage interviews with candidates',
          badge: null
        },
        {
          id: 'ai-matching',
          label: 'AI Matching',
          icon: 'Sparkles',
          path: '/auto-matching',
          description: 'Find perfect candidates with AI recommendations',
          badge: null
        }
      ];
    } else if (userRole === 'admin') {
      return [
        {
          id: 'dashboard',
          label: 'Admin Dashboard',
          icon: 'LayoutDashboard',
          path: '/admin-moderation?tab=dashboard',
          description: 'System overview and platform management',
          badge: null
        },
        {
          id: 'users',
          label: 'User Management',
          icon: 'Users',
          path: '/admin-moderation?tab=users',
          description: 'Manage users, roles, and permissions',
          badge: notifications.filter(n => n.type === 'user_report' && n.unread).length || null
        },
        {
          id: 'content',
          label: 'Content Moderation',
          icon: 'Shield',
          path: '/admin-moderation?tab=reports',
          description: 'Review and moderate platform content',
          badge: notifications.filter(n => n.type === 'moderation' && n.unread).length || null
        },
        {
          id: 'jobs',
          label: 'Job Management',
          icon: 'Briefcase',
          path: '/admin-moderation?tab=jobs',
          description: 'Monitor and manage job postings',
          badge: notifications.filter(n => n.type === 'job_report' && n.unread).length || null
        },
        {
          id: 'job-moderation',
          label: 'Job Moderation',
          icon: 'ClipboardCheck',
          path: '/admin-moderation?tab=job-moderation',
          description: 'Review job postings from free tier employers',
          badge: notifications.filter(n => n.type === 'job_moderation' && n.unread).length || null
        },
        {
          id: 'analytics',
          label: 'Platform Analytics',
          icon: 'BarChart3',
          path: '/admin-moderation?tab=analytics',
          description: 'Platform metrics and system insights',
          badge: null
        },
        {
          id: 'upgrade-analytics',
          label: 'Upgrade Analytics',
          icon: 'TrendingUp',
          path: '/admin-moderation?tab=analytics',
          description: 'Track free tier conversions and engagement metrics',
          badge: null
        },
        {
          id: 'support',
          label: 'Customer Support',
          icon: 'HeadphonesIcon',
          path: '/admin/support',
          description: 'Manage customer conversations and support tickets',
          badge: null
        },
        {
          id: 'reports',
          label: 'Reports & Logs',
          icon: 'FileText',
          path: '/admin-moderation?tab=reports',
          description: 'View system reports and audit logs',
          badge: notifications.filter(n => n.type === 'system_alert' && n.unread).length || null
        },
        {
          id: 'settings',
          label: 'System Settings',
          icon: 'Settings',
          path: '/admin/settings',
          description: 'Configure platform settings and features',
          badge: null
        }
      ];
    } else if (userRole === 'moderator') {
      return [
        {
          id: 'dashboard',
          label: 'Moderation Dashboard',
          icon: 'LayoutDashboard',
          path: '/admin-moderation?tab=dashboard',
          description: 'Overview of moderation activities',
          badge: null
        },
        {
          id: 'live-chat',
          label: 'Live Chat',
          icon: 'Headphones',
          path: '/moderator/chat',
          description: 'Respond to customer conversations',
          badge: notifications.filter(n => n.type === 'chat' && n.unread).length || null
        },
        {
          id: 'queue',
          label: 'Review Queue',
          icon: 'AlertTriangle',
          path: '/admin-moderation?tab=reports',
          description: 'Review flagged content and reports',
          badge: notifications.filter(n => n.type === 'moderation' && n.unread).length || null
        },
        {
          id: 'profiles',
          label: 'Profile Reviews',
          icon: 'UserCheck',
          path: '/moderator/profiles',
          description: 'Review VA and employer profiles',
          badge: notifications.filter(n => n.type === 'profile_review' && n.unread).length || null
        },
        {
          id: 'jobs',
          label: 'Job Reviews',
          icon: 'Briefcase',
          path: '/admin-moderation?tab=jobs',
          description: 'Review and approve job postings',
          badge: notifications.filter(n => n.type === 'job_review' && n.unread).length || null
        },
        {
          id: 'messages',
          label: 'Messages',
          icon: 'MessageSquare',
          path: '/messages',
          description: 'Communication and support',
          badge: notifications.filter(n => n.type === 'message' && n.unread).length || null
        },
        {
          id: 'reports',
          label: 'Reports',
          icon: 'FileText',
          path: '/admin-moderation?tab=reports',
          description: 'View moderation reports and history',
          badge: null
        }
      ];
    }
    return [];
  };

  const navigationItems = getNavigationItems();

  // Quick actions based on user role
  const getQuickActions = () => {
    if (!currentUser) return []; // Return empty array for non-authenticated users
    
    const userRole = userProfile?.role || currentUser?.role || 'va';
    
    if (userRole === 'va') {
      return [
        { 
          label: 'Apply to Job', 
          icon: 'Plus', 
          action: () => navigate('/job-search'),
          description: 'Browse and apply to new opportunities'
        },
        { 
          label: 'Update Profile', 
          icon: 'Edit', 
          action: () => navigate('/va-profile'),
          description: 'Enhance your portfolio and visibility'
        },
        { 
          label: 'Add Skills', 
          icon: 'Award', 
          action: () => navigate('/up-skills'),
          description: 'Boost credibility with skill verification'
        },
        { 
          label: 'Interviews', 
          icon: 'BarChart3', 
          action: () => navigate('/interviews'),
          description: 'Manage your interviews with employers'
        }
      ];
    } else if (userRole === 'admin') {
      return [
        { 
          label: 'User Management', 
          icon: 'UserPlus', 
          action: () => navigate('/admin-moderation?tab=users'),
          description: 'Manage user accounts and permissions'
        },
        { 
          label: 'Content Review', 
          icon: 'Shield', 
          action: () => navigate('/admin-moderation?tab=reports'),
          description: 'Review flagged content and reports'
        },
        { 
          label: 'System Analytics', 
          icon: 'BarChart3', 
          action: () => navigate('/admin-moderation?tab=analytics'),
          description: 'View platform performance metrics'
        },
        { 
          label: 'Platform Settings', 
          icon: 'Settings', 
          action: () => navigate('/admin/settings'),
          description: 'Configure system-wide settings'
        }
      ];
    } else if (userRole === 'moderator') {
      return [
        { 
          label: 'Live Chat', 
          icon: 'Headphones', 
          action: () => navigate('/moderator/chat'),
          description: 'Respond to customer conversations'
        },
        { 
          label: 'Review Queue', 
          icon: 'AlertTriangle', 
          action: () => navigate('/admin-moderation?tab=dashboard'),
          description: 'Process pending content reviews'
        },
        { 
          label: 'Profile Reviews', 
          icon: 'UserCheck', 
          action: () => navigate('/admin-moderation?tab=users'),
          description: 'Approve user profiles and updates'
        },
        { 
          label: 'Job Approval', 
          icon: 'CheckCircle', 
          action: () => navigate('/admin-moderation?tab=jobs'),
          description: 'Review and approve job postings'
        }
      ];
    } else {
      return [
        { 
          label: 'Post New Job', 
          icon: 'Plus', 
          action: () => navigate('/job-posting'),
          description: 'Find the perfect virtual assistant'
        },
        { 
          label: 'Search Candidates', 
          icon: 'Search', 
          action: () => navigate('/candidate-search'),
          description: 'Browse qualified VA profiles'
        },
        { 
          label: 'Schedule Interview', 
          icon: 'Calendar', 
          action: () => navigate('/interview-management'),
          description: 'Manage candidate interviews'
        },
        { 
          label: 'AI Matching', 
          icon: 'Sparkles', 
          action: () => navigate('/auto-matching'),
          description: 'Find perfect candidates with AI recommendations'
        },
        { 
          label: 'View Reports', 
          icon: 'BarChart3', 
          action: () => navigate('/advanced-analytics'),
          description: 'Analyze hiring performance'
        }
      ];
    }
  };

  const quickActions = getQuickActions();

  // Helper function for action descriptions
  const getActionDescription = (label, role) => {
    const descriptions = {
      'va': {
        'Apply to Job': 'Browse and apply to new opportunities',
        'Update Profile': 'Enhance your portfolio and visibility',
        'Take Assessment': 'Boost credibility with skill verification',
        'View Analytics': 'Track your job search progress'
      },
      'employer': {
        'Post New Job': 'Find the perfect virtual assistant',
        'Search Candidates': 'Browse qualified VA profiles',
        'Schedule Interview': 'Manage candidate interviews',
        'AI Matching': 'Find perfect candidates with AI recommendations',
        'View Reports': 'Analyze hiring performance'
      },
      'admin': {
        'User Management': 'Manage user accounts and permissions',
        'Content Review': 'Review flagged content and reports',
        'System Analytics': 'View platform performance metrics',
        'Platform Settings': 'Configure system-wide settings'
      },
      'moderator': {
        'Review Queue': 'Process pending content reviews',
        'Profile Reviews': 'Approve user profiles and updates',
        'Job Approval': 'Review and approve job postings',
        'Generate Report': 'Create moderation activity reports'
      }
    };
    return descriptions[role]?.[label] || 'Quick action';
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Get current workflow step for user journey tracking
  const getCurrentWorkflowStep = () => {
    if (!currentUser) return { step: 'exploring', next: null }; // Default for non-authenticated users
    
    const currentPath = location.pathname;
    const userRole = userProfile?.role || 'va';
    
    if (userRole === 'va') {
      if (currentPath === '/va-dashboard') return { step: 'overview', next: '/job-search' };
      if (currentPath === '/job-search') return { step: 'searching', next: '/applications' };
      if (currentPath.startsWith('/job-details')) return { step: 'reviewing', next: '/applications' };
      if (currentPath === '/applications') return { step: 'tracking', next: '/messages' };
      if (currentPath === '/messages') return { step: 'communicating', next: '/interviews' };
      if (currentPath === '/interviews') return { step: 'interviewing', next: '/va-profile' };
      if (currentPath === '/va-profile') return { step: 'showcasing', next: '/skills-assessment' };
      if (currentPath === '/skills-assessment') return { step: 'validating', next: '/analytics' };
    } else if (userRole === 'employer') {
      if (currentPath === '/employer-dashboard') return { step: 'overview', next: '/job-posting' };
      if (currentPath === '/job-posting') return { step: 'posting', next: '/candidate-search' };
      if (currentPath === '/candidate-search') return { step: 'sourcing', next: '/messages' };
      if (currentPath === '/messages') return { step: 'communicating', next: '/interview-management' };
      if (currentPath === '/interview-management') return { step: 'interviewing', next: '/advanced-analytics' };
      if (currentPath === '/advanced-analytics') return { step: 'analyzing', next: '/employer-dashboard' };
    } else if (userRole === 'admin') {
      if (currentPath === '/admin-dashboard') return { step: 'monitoring', next: '/admin/users' };
      if (currentPath === '/admin/users') return { step: 'user_management', next: '/admin/moderation' };
      if (currentPath === '/admin/moderation') return { step: 'content_review', next: '/admin/jobs' };
      if (currentPath === '/admin/jobs') return { step: 'job_oversight', next: '/admin/analytics' };
      if (currentPath === '/admin/analytics') return { step: 'analyzing', next: '/admin/reports' };
      if (currentPath === '/admin/reports') return { step: 'reporting', next: '/admin/settings' };
      if (currentPath === '/admin/settings') return { step: 'configuring', next: '/admin-dashboard' };
    } else if (userRole === 'moderator') {
      if (currentPath === '/moderator-dashboard') return { step: 'overview', next: '/moderator/queue' };
      if (currentPath === '/moderator/queue') return { step: 'reviewing', next: '/moderator/profiles' };
      if (currentPath === '/moderator/profiles') return { step: 'profile_review', next: '/moderator/jobs' };
      if (currentPath === '/moderator/jobs') return { step: 'job_approval', next: '/messages' };
      if (currentPath === '/messages') return { step: 'communicating', next: '/moderator/reports' };
      if (currentPath === '/moderator/reports') return { step: 'reporting', next: '/moderator-dashboard' };
    }
    
    return { step: 'exploring', next: null };
  };

  // Add keyboard shortcuts support
  useEffect(() => {
    // Only add keyboard shortcuts for authenticated users
    if (!currentUser) return;
    
    const handleKeyDown = (event) => {
      // Only handle if Alt key is pressed and no input is focused
      if (!event.altKey || event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      const userRole = userProfile?.role || 'va';
      
      switch (event.key.toLowerCase()) {
        case 'h':
          event.preventDefault();
          if (userRole === 'va') navigate('/va-dashboard');
          else if (userRole === 'employer') navigate('/employer-dashboard');
          else if (userRole === 'admin') navigate('/admin-moderation?tab=dashboard');
          else if (userRole === 'moderator') navigate('/admin-moderation?tab=reports');
          break;
        case 's':
          event.preventDefault();
          if (userRole === 'va') navigate('/job-search');
          else if (userRole === 'employer') navigate('/candidate-search');
          else if (userRole === 'admin') navigate('/admin/users');
          else if (userRole === 'moderator') navigate('/moderator/queue');
          break;
        case 'm':
          event.preventDefault();
          navigate('/messages');
          break;
        case 'p':
          event.preventDefault();
          if (userRole === 'va') navigate('/va-profile');
          else if (userRole === 'employer') navigate('/employer-profile-settings');
          else if (userRole === 'admin') navigate('/admin/settings');
          else if (userRole === 'moderator') navigate('/moderator/profiles');
          break;
        case 'a':
          event.preventDefault();
          if (userRole === 'va') navigate('/analytics');
          else if (userRole === 'employer') navigate('/advanced-analytics');
          else if (userRole === 'admin') navigate('/admin/analytics');
          else if (userRole === 'moderator') navigate('/moderator/reports');
          break;
        case 'q':
          event.preventDefault();
          setIsQuickActionsOpen(!isQuickActionsOpen);
          break;
        case 'r':
          event.preventDefault();
          setIsRecentPagesOpen(!isRecentPagesOpen);
          break;
        case 'n':
          event.preventDefault();
          setIsNotificationsOpen(!isNotificationsOpen);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, userProfile?.role, isQuickActionsOpen, isRecentPagesOpen, isNotificationsOpen, currentUser]);

  return (
    <>
      {/* Desktop Navigation */}
      <div className="hidden md:block bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 group relative">
                <button
                  onClick={() => {
                    if (currentUser && userProfile?.role) {
                      const dashboardPath = userProfile.role === 'va' ? '/va-dashboard' : 
                      userProfile.role === 'employer' ? '/employer-dashboard' : 
                      userProfile.role === 'admin' ? '/admin-moderation?tab=dashboard' : 
                      userProfile.role === 'moderator' ? '/admin-moderation?tab=reports' : // Moderators start with reports queue
                      '/user-dashboard';
                      navigate(dashboardPath);
                    }
                  }}
                  className="w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                  title={currentUser && userProfile?.role ? `Go to ${
                    userProfile.role === 'va' ? 'VA' : 
                    userProfile.role === 'employer' ? 'Employer' :
                    userProfile.role === 'admin' ? 'Admin' :
                    userProfile.role === 'moderator' ? 'Moderator' : 'User'
                  } Dashboard` : 'VA Sprouts'}
                >
                  <img
                    src="/assets/images/Logo/webp/VAS-LOGO.webp"
                    alt="VA Sprouts Logo"
                    className="w-full h-full object-contain"
                  />
                </button>
                <div>
                  <button
                    onClick={() => {
                      if (currentUser && userProfile?.role) {
                        const dashboardPath = userProfile.role === 'va' ? '/va-dashboard' : 
                        userProfile.role === 'employer' ? '/employer-dashboard' : 
                        userProfile.role === 'admin' ? '/admin-moderation?tab=dashboard' : 
                        userProfile.role === 'moderator' ? '/admin-moderation?tab=reports' : 
                        '/user-dashboard';
                        navigate(dashboardPath);
                      }
                    }}
                    className={`text-xl font-bold text-gray-900 ${currentUser && userProfile?.role ? 'hover:text-blue-600 cursor-pointer ' : ''}`}
                    title={currentUser && userProfile?.role ? `Go to ${
                      userProfile.role === 'va' ? 'VA' : 
                      userProfile.role === 'employer' ? 'Employer' :
                      userProfile.role === 'admin' ? 'Admin' :
                      userProfile.role === 'moderator' ? 'Moderator' : 'User'
                    } Dashboard` : undefined}
                  >
                    VA Sprouts
                  </button>
                  {userProfile?.role && (
                    <div className="text-xs text-gray-500">
                      {userProfile.role === 'va' ? 'Virtual Assistant Hub' : 
                       userProfile.role === 'employer' ? 'Employer Portal' :
                       userProfile.role === 'admin' ? 'Administrator Panel' :
                       userProfile.role === 'moderator' ? 'Moderation Center' : 'User Portal'}
                    </div>
                  )}
                </div>
                
                {/* Keyboard shortcuts help tooltip - only for authenticated users */}
                {currentUser && (
                  <div className="invisible group-hover:visible absolute top-full left-0 mt-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50 transition-all duration-200">
                    <div className="font-semibold mb-2">Keyboard Shortcuts:</div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>Alt + H</span>
                        <span>Dashboard</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Alt + S</span>
                        <span>
                          {userProfile?.role === 'va' ? 'Search Jobs' : 
                           userProfile?.role === 'employer' ? 'Find Candidates' :
                           userProfile?.role === 'admin' ? 'User Management' :
                           userProfile?.role === 'moderator' ? 'Review Queue' : 'Search'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Alt + M</span>
                        <span>Messages</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Alt + P</span>
                        <span>
                          {userProfile?.role === 'va' ? 'Profile' :
                           userProfile?.role === 'employer' ? 'Profile Settings' :
                           userProfile?.role === 'admin' ? 'System Settings' :
                           userProfile?.role === 'moderator' ? 'Profile Reviews' : 'Profile'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Alt + A</span>
                        <span>
                          {userProfile?.role === 'va' ? 'Analytics' :
                           userProfile?.role === 'employer' ? 'Analytics' :
                           userProfile?.role === 'admin' ? 'Platform Analytics' :
                           userProfile?.role === 'moderator' ? 'Reports' : 'Analytics'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Alt + Q</span>
                        <span>Quick Actions</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Alt + R</span>
                        <span>Recent Pages</span>
                      </div>
                    </div>
                    <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </div>
              
              {/* Current workflow indicator - only for authenticated users */}
              {currentUser && location.pathname !== '/va-dashboard' && location.pathname !== '/employer-dashboard' && (
                <div className="hidden lg:flex items-center space-x-2 text-sm text-gray-500">
                  <_Icon name="ChevronRight" size={16} />
                  <span className="font-medium">
                    {navigationItems.find(item => isActivePath(item.path))?.label || 'Current Page'}
                  </span>
                  {getCurrentWorkflowStep().next && (
                    <div className="flex items-center space-x-1 ml-3 text-xs">
                      <span className="text-gray-400">Next:</span>
                      <button
                        onClick={() => handleNavigation(getCurrentWorkflowStep().next)}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {navigationItems.find(item => item.path === getCurrentWorkflowStep().next)?.label}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation Items Removed - Using dropdowns and mobile nav instead */}
            <div className="flex items-center">
              {/* Spacer to center the right-side actions */}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              {currentUser ? (
                <>
                  {/* Authenticated User Actions */}
                  {/* Recent Pages */}
                  <div className="relative" ref={recentPagesRef}>
                    <button
                      onClick={() => setIsRecentPagesOpen(!isRecentPagesOpen)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg "
                      title="Recent Pages"
                    >
                      <_Icon name="Clock" size={20} />
                    </button>
                    
                    {isRecentPagesOpen && (
                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                          Recently Visited
                        </div>
                        {recentlyVisited.length > 0 ? (
                          <>
                            {recentlyVisited.slice(0, 5).map((page, index) => (
                              <button
                                key={`${page.path}-${index}`}
                                onClick={() => {
                                  handleNavigation(page.path);
                                  setIsRecentPagesOpen(false);
                                }}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 "
                              >
                                <span className="truncate font-medium">{page.title}</span>
                                <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
                                  {new Date(page.timestamp).toLocaleTimeString([], { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </span>
                              </button>
                            ))}
                            <div className="border-t border-gray-100 mt-2 pt-2">
                              <button
                                onClick={() => {
                                  // Clear recent history
                                  setIsRecentPagesOpen(false);
                                }}
                                className="w-full px-3 py-1 text-xs text-gray-500 hover:text-gray-700 "
                              >
                                Clear History
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="px-3 py-4 text-sm text-gray-500 text-center">
                            No recent pages
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Navigation Suggestions */}
                  <div className="relative" ref={suggestionsRef}>
                    <button
                      onClick={() => {
                        setIsSuggestionsOpen(!isSuggestionsOpen);
                      }}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg "
                      title="Smart Suggestions"
                    >
                      <_Icon name="Lightbulb" size={20} />
                    </button>
                    
                    {isSuggestionsOpen && (
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                          Suggested for You
                        </div>
                        {getNavigationSuggestions().length > 0 ? (
                          getNavigationSuggestions().map((suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                handleNavigation(suggestion.path);
                                setIsSuggestionsOpen(false);
                              }}
                              className="w-full text-left p-3 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50  border-b border-gray-50 last:border-b-0"
                            >
                              <div className="font-medium text-sm text-gray-900 mb-1">
                                {suggestion.label}
                              </div>
                              <div className="text-xs text-gray-600">
                                {suggestion.reason}
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-4 text-sm text-gray-500 text-center">
                            No suggestions available
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="relative" ref={quickActionsRef}>
                    <button
                      onClick={() => setIsQuickActionsOpen(!isQuickActionsOpen)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg "
                      title="Quick Actions"
                    >
                      <_Icon name="Zap" size={20} />
                    </button>
                    
                    {isQuickActionsOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                          Quick Actions
                        </div>
                        {quickActions.map((action, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              action.action();
                              setIsQuickActionsOpen(false);
                            }}
                            className="w-full flex items-center space-x-3 px-3 py-3 text-sm text-gray-700 hover:bg-gray-50  group"
                          >
                            <div className="p-1.5 rounded-md bg-gray-100 group-hover:bg-blue-100 ">
                              <_Icon name={action.icon} size={16} className="group-hover:text-blue-600" />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="font-medium">{action.label}</div>
                              <div className="text-xs text-gray-500">{action.description || getActionDescription(action.label, userProfile?.role)}</div>
                            </div>
                          </button>
                        ))}
                        <div className="border-t border-gray-100 mt-2 pt-2">
                          <button
                            onClick={() => {
                              setIsSettingsOpen(true);
                              setIsQuickActionsOpen(false);
                            }}
                            className="w-full flex items-center space-x-3 px-3 py-2 text-xs text-gray-500 hover:text-gray-700 "
                          >
                            <_Icon name="Settings" size={14} />
                            <span>Customize Quick Actions</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notifications */}
                  <div className="relative" ref={notificationsRef}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsNotificationsOpen(!isNotificationsOpen);
                      }}
                      className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg "
                      title="Notifications"
                      style={{ pointerEvents: 'auto', cursor: 'pointer', zIndex: 1 }}
                    >
                      <_Icon name="Bell" size={20} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                      {getHighPriorityNotifications().length > 0 && (
                        <span className="absolute -bottom-1 -left-1 bg-yellow-500 w-2 h-2 rounded-full"></span>
                      )}
                    </button>
                    
                    <_NotificationDropdown 
                      isOpen={isNotificationsOpen} 
                      setIsOpen={setIsNotificationsOpen} 
                      showViewAllLink={true}
                      viewAllLink="/notifications"
                    />
                  </div>
                  {/* User Menu */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 "
                      aria-expanded={isUserMenuOpen}
                      aria-haspopup="true"
                      aria-label="User menu"
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium shadow-sm border border-white overflow-hidden">
                        {userProfile?.profile_picture ? (
                          <img
                            src={userProfile.profile_picture}
                            alt={userProfile?.full_name || 'User'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center ${userProfile?.profile_picture ? 'hidden' : ''}`}
                          style={{ display: userProfile?.profile_picture ? 'none' : 'flex' }}
                        >
                          {userProfile?.full_name?.charAt(0).toUpperCase() || currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      </div>
                      {userProfile?.role && (
                        <span className="hidden md:inline-flex text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                          {userProfile.role === 'va' ? 'VA' : userProfile.role === 'employer' ? 'Employer' : userProfile.role}
                        </span>
                      )}
                    </button>
                    
                    {/* User menu dropdown content */}
                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200 max-h-80 overflow-y-auto">
                        {/* User Info Header */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-medium shadow-sm border-2 border-white overflow-hidden">
                              {userProfile?.profile_picture ? (
                                <img
                                  src={userProfile.profile_picture}
                                  alt={userProfile?.full_name || 'User'}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div
                                className={`w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center ${userProfile?.profile_picture ? 'hidden' : ''}`}
                                style={{ display: userProfile?.profile_picture ? 'none' : 'flex' }}
                              >
                                {userProfile?.full_name?.charAt(0).toUpperCase() || currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {userProfile?.full_name || 'User'}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {currentUser?.email}
                              </p>
                              {userProfile?.role && (
                                <span className="inline-flex text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 mt-1">
                                  {userProfile.role === 'va' ? 'Virtual Assistant' : 
                                   userProfile.role === 'employer' ? 'Employer' :
                                   userProfile.role === 'admin' ? 'Administrator' :
                                   userProfile.role === 'moderator' ? 'Moderator' :
                                   userProfile.role.charAt(0).toUpperCase() + userProfile.role.slice(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Profile Management */}
                        <div className="px-1 py-2">
                          <button
                            onClick={() => {
                              if (userProfile?.role === 'va') navigate('/va-profile-settings');
                              else if (userProfile?.role === 'employer') navigate('/employer-profile-settings');
                              else if (userProfile?.role === 'admin') navigate('/admin/profile-settings');
                              else if (userProfile?.role === 'moderator') navigate('/admin/profile-settings');
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                          >
                            <_Icon name="Settings" size={16} />
                            <div className="flex-1 text-left">
                              <div className="font-medium">
                                {userProfile?.role === 'va' ? 'Profile Settings' : 
                                 userProfile?.role === 'employer' ? 'Profile Settings' :
                                 userProfile?.role === 'admin' ? 'Profile Settings' :
                                 userProfile?.role === 'moderator' ? 'Profile Settings' : 'Update settings'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {userProfile?.role === 'va' ? 'Manage your skills and portfolio' : 
                                 userProfile?.role === 'employer' ? 'Update company information' :
                                 userProfile?.role === 'admin' ? 'Manage account and security settings' :
                                 userProfile?.role === 'moderator' ? 'Manage account and security settings' : 'Update settings'}
                              </div>
                            </div>
                          </button>
                          
                          {userProfile?.role === 'va' && (
                            <button
                              onClick={() => {
                                navigate('/va-profile');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="User" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">View Public Profile</div>
                                <div className="text-xs text-gray-500">
                                  See how employers view your profile
                                </div>
                              </div>
                            </button>
                          )}
                          
                          {(userProfile?.role === 'admin' || userProfile?.role === 'moderator') && (
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=dashboard');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="LayoutDashboard" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Admin Dashboard</div>
                                <div className="text-xs text-gray-500">
                                  {userProfile?.role === 'admin' ? 'Manage system and users' : 'Review reports and moderation'}
                                </div>
                              </div>
                            </button>
                          )}
                        </div>

                        {/* Role-specific menu items */}
                        {userProfile?.role === 'va' ? (
                          <div className="border-t border-gray-100 px-1 py-2">
                            <button
                              onClick={() => {
                                navigate('/applications');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="FileText" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">My Applications</div>
                                <div className="text-xs text-gray-500">Track job applications and interviews</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/up-skills');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Award" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Manage Skills</div>
                                <div className="text-xs text-gray-500">Validate your skills and certifications</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/analytics');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="BarChart3" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Career Analytics</div>
                                <div className="text-xs text-gray-500">View your job search metrics</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/va-portfolio-management');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Briefcase" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Portfolio</div>
                                <div className="text-xs text-gray-500">Showcase your work and achievements</div>
                              </div>
                            </button>
                          </div>
                        ) : userProfile?.role === 'employer' ? (
                          <div className="border-t border-gray-100 px-1 py-2">
                            <button
                              onClick={() => {
                                navigate('/job-posting');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Plus" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Post New Job</div>
                                <div className="text-xs text-gray-500">Create a new job listing</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/employer-dashboard');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Briefcase" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Manage Jobs</div>
                                <div className="text-xs text-gray-500">View and edit your job postings</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/candidate-search');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Users" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Find Candidates</div>
                                <div className="text-xs text-gray-500">Search and review VA profiles</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/interview-management');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Calendar" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Interview Management</div>
                                <div className="text-xs text-gray-500">Schedule and manage interviews</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/advanced-analytics');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="BarChart3" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Hiring Analytics</div>
                                <div className="text-xs text-gray-500">View hiring metrics and insights</div>
                              </div>
                            </button>
                          </div>
                        ) : userProfile?.role === 'admin' ? (
                          <div className="border-t border-gray-100 px-1 py-2">
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=users');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Users" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">User Management</div>
                                <div className="text-xs text-gray-500">Manage users, roles, and permissions</div>
                              </div>
                            </button>
                        
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=jobs');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Briefcase" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Job Management</div>
                                <div className="text-xs text-gray-500">Monitor and manage job postings</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=job-moderation');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="ClipboardCheck" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Job Moderation</div>
                                <div className="text-xs text-gray-500">Review free tier job postings</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=analytics');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="BarChart3" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Platform Analytics</div>
                                <div className="text-xs text-gray-500">View platform metrics and system insights</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/admin/support');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="HeadphonesIcon" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Customer Support</div>
                                <div className="text-xs text-gray-500">Manage support conversations and tickets</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/admin/settings');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Settings" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">System Settings</div>
                                <div className="text-xs text-gray-500">Configure platform settings and features</div>
                              </div>
                            </button>
                          </div>
                        ) : userProfile?.role === 'moderator' ? (
                          <div className="border-t border-gray-100 px-1 py-2">
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=dashboard');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="AlertTriangle" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Review Queue</div>
                                <div className="text-xs text-gray-500">Review flagged content and reports</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=users');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="UserCheck" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Profile Reviews</div>
                                <div className="text-xs text-gray-500">Review VA and employer profiles</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=jobs');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="Briefcase" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Job Reviews</div>
                                <div className="text-xs text-gray-500">Review and approve job postings</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=job-moderation');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="ClipboardCheck" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Job Moderation</div>
                                <div className="text-xs text-gray-500">Moderate free tier job postings</div>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/admin-moderation?tab=reports');
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                            >
                              <_Icon name="FileText" size={16} />
                              <div className="flex-1 text-left">
                                <div className="font-medium">Moderation Reports</div>
                                <div className="text-xs text-gray-500">View moderation reports and history</div>
                              </div>
                            </button>
                          </div>
                        ) : null}

                        {/* Account & Settings */}
                        <div className="border-t border-gray-100 px-1 py-2">
                          <button
                            onClick={() => {
                              navigate('/notifications');
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                          >
                            <_Icon name="Bell" size={16} />
                            <div className="flex-1 text-left">
                              <div className="font-medium flex items-center space-x-2">
                                <span>Notifications</span>
                                {unreadCount > 0 && (
                                  <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">Manage your notification preferences</div>
                            </div>
                          </button>
                          
                          <button
                            onClick={() => {
                              navigate('/help-support');
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md "
                          >
                            <_Icon name="HelpCircle" size={16} />
                            <div className="flex-1 text-left">
                              <div className="font-medium">Help & Support</div>
                              <div className="text-xs text-gray-500">Get help and contact support</div>
                            </div>
                          </button>
                        </div>

                        {/* Sign out button */}
                        <div className="border-t border-gray-100 pt-2">
                          <button
                            onClick={() => {
                              signOut();
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 "
                          >
                            <_Icon name="LogOut" size={16} />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Public User Actions - Login and Sign Up buttons */}
                  <_Button
                    variant="outline"
                    size="sm"
                    className="mr-2"
                    onClick={() => navigate('/login-register?redirect=' + encodeURIComponent(window.location.pathname))}
                  >
                    Sign In
                  </_Button>
                  <_Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/login-register?mode=register&redirect=' + encodeURIComponent(window.location.pathname))}
                  >
                    Sign Up
                  </_Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header Navigation */}
      <div className="md:hidden bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Left Side - Logo and Current Page */}
          <div className="flex items-center space-x-3 flex-1">
            <button
              onClick={() => {
                if (currentUser && userProfile?.role) {
                  const dashboardPath = userProfile.role === 'va' ? '/va-dashboard' : 
                  userProfile.role === 'employer' ? '/employer-dashboard' : 
                  userProfile.role === 'admin' ? '/admin-moderation?tab=dashboard' : 
                  userProfile.role === 'moderator' ? '/admin-moderation?tab=reports' : 
                  '/user-dashboard';
                  navigate(dashboardPath);
                }
              }}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity"
            >
              <img
                src="/assets/images/Logo/webp/VAS-LOGO.webp"
                alt="VA Sprouts Logo"
                className="w-full h-full object-contain"
              />
            </button>
            
            {currentUser ? (
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {navigationItems.find(item => isActivePath(item.path))?.label || 'VA Sprouts'}
                  </span>
                  {userProfile?.role && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
                      {userProfile.role === 'va' ? 'VA' : 
                       userProfile.role === 'employer' ? 'Employer' :
                       userProfile.role === 'admin' ? 'Admin' :
                       userProfile.role === 'moderator' ? 'Mod' : 'User'}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {navigationItems.find(item => isActivePath(item.path))?.description || 'Welcome back'}
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <div className="text-lg font-bold text-gray-900">VA Sprouts</div>
                <div className="text-xs text-gray-500">Find Your Perfect Match</div>
              </div>
            )}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2">
            {currentUser ? (
              <>
                {/* Notifications */}
                <div className="relative" ref={notificationsRef}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsNotificationsOpen(!isNotificationsOpen);
                    }}
                    className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg "
                    style={{ pointerEvents: 'auto', cursor: 'pointer', zIndex: 1 }}
                  >
                    <_Icon name="Bell" size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  
                  <_NotificationDropdown 
                    isOpen={isNotificationsOpen} 
                    setIsOpen={setIsNotificationsOpen} 
                    showViewAllLink={true}
                    viewAllLink="/notifications"
                  />
                </div>

                {/* Mobile Menu Button */}
                <div className="relative" ref={mobileMenuRef}>
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg "
                  >
                    <_Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
                  </button>

                  {/* Mobile Menu Dropdown */}
                  {isMobileMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium overflow-hidden">
                            {userProfile?.profile_picture ? (
                              <img
                                src={userProfile.profile_picture}
                                alt={userProfile?.full_name || 'User'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center ${userProfile?.profile_picture ? 'hidden' : ''}`}
                              style={{ display: userProfile?.profile_picture ? 'none' : 'flex' }}
                            >
                              {userProfile?.full_name?.charAt(0).toUpperCase() || currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {userProfile?.full_name || 'User'}
                            </p>
                            <p className="text-xs text-gray-600 truncate">
                              {currentUser?.email}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions Section */}
                      <div className="px-1 py-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Quick Actions
                        </div>
                        {quickActions.slice(0, 4).map((action, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              action.action();
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full flex items-center space-x-3 px-3 py-3 text-sm text-gray-700 hover:bg-gray-50  group"
                          >
                            <div className="p-2 rounded-lg bg-blue-100 group-hover:bg-blue-200 ">
                              <_Icon name={action.icon} size={16} className="text-blue-600" />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="font-medium">{action.label}</div>
                              <div className="text-xs text-gray-500">{action.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Navigation Items */}
                      <div className="border-t border-gray-100 px-1 py-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Navigate To
                        </div>
                        {navigationItems.map(item => (
                          <button
                            key={item.id}
                            onClick={() => {
                              handleNavigation(item.path);
                              setIsMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center space-x-3 px-3 py-3 text-sm  rounded-md mx-2 ${
                              isActivePath(item.path)
                                ? 'bg-blue-50 text-blue-600 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <div className={`p-1.5 rounded-md  ${
                              isActivePath(item.path)
                                ? 'bg-blue-100'
                                : 'bg-gray-100'
                            }`}>
                              <_Icon 
                                name={item.icon} 
                                size={16} 
                                className={isActivePath(item.path) ? 'text-blue-600' : 'text-gray-600'} 
                              />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="font-medium flex items-center justify-between">
                                <span>{item.label}</span>
                                {item.badge && item.badge > 0 && (
                                  <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium ml-2">
                                    {item.badge > 9 ? '9+' : item.badge}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">{item.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Profile Management */}
                      <div className="border-t border-gray-100 px-1 py-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Account
                        </div>
                        <button
                          onClick={() => {
                            if (userProfile?.role === 'va') navigate('/va-profile-settings');
                            else if (userProfile?.role === 'employer') navigate('/employer-profile-settings');
                            else if (userProfile?.role === 'admin') navigate('/admin/profile-settings');
                            else if (userProfile?.role === 'moderator') navigate('/moderator/profile-settings');
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md mx-2 "
                        >
                          <_Icon name="Settings" size={16} />
                          <span>Profile Settings</span>
                        </button>
                        
                        {/* <button
                          onClick={() => {
                            navigate(getProfileSettingsUrl());
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md mx-2 "
                        >
                          <_Icon name="Cog" size={16} />
                          <span>Profile Settings</span>
                        </button> */}
                        
                        <button
                          onClick={() => {
                            navigate('/help-support');
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md mx-2 "
                        >
                          <_Icon name="HelpCircle" size={16} />
                          <span>Help & Support</span>
                        </button>
                      </div>

                      {/* Sign Out */}
                      <div className="border-t border-gray-100 px-1 py-2">
                        <button
                          onClick={() => {
                            signOut();
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md mx-2 "
                        >
                          <_Icon name="LogOut" size={16} />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Public User - Login Button */}
                <button
                  onClick={() => navigate('/login-register?redirect=' + encodeURIComponent(window.location.pathname))}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-50 "
                >
                  Sign In
                </button>
              </>
            )}
          </div>
        </div>

        
      </div>

      {/* Mobile Navigation - Enhanced and conditionally rendered */}
      {currentUser && userProfile?.role !== 'admin' ? (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
          <div className="flex items-center justify-around px-2 py-1">
            {navigationItems.map(item => {
              const isActive = isActivePath(item.path);
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex flex-col items-center justify-center flex-1 py-2 px-1 relative  duration-200 ${
                    isActive ? 'text-blue-600' : 'text-gray-600'
                  }`}
                >
                  <div className="relative">
                    <_Icon 
                      name={item.icon} 
                      size={22} 
                      strokeWidth={isActive ? 2.5 : 2}
                    />
                    {item.badge && item.badge > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-medium">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs mt-1 font-medium truncate max-w-full ${
                    isActive ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    {item.label}
                  </span>
                  
                  {isActive && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-blue-600 rounded-full"></div>
                  )}
                </button>
              );
            })}
          </div>
          
          
        </div>
      ) : !currentUser ? (
        <div className="md:hidden absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 py-2 px-4">
          <div className="flex items-center justify-between">
            <_Button
              variant="outline"
              size="sm"
              className="flex-1 mr-2"
              onClick={() => navigate('/login-register?redirect=' + encodeURIComponent(window.location.pathname))}
            >
              Sign In
            </_Button>
            <_Button
              variant="primary"
              size="sm"
              className="flex-1"
              onClick={() => navigate('/login-register?mode=register&redirect=' + encodeURIComponent(window.location.pathname))}
            >
              Sign Up
            </_Button>
          </div>
        </div>
      ) : null}

      {/* Navigation Settings Modal */}
      <_NavigationSettings 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />
    </>
  );
};

export default EnhancedNavigation;
